// Single seam between the app and the analytics subgraph.
//
// design.md "Containment rule" — no non-analytics file may statically import
// from `@services/analytics/*`. Every call site needs the same gate +
// `import('@services/analytics/events')` pattern; centralising it here keeps
// the pattern correct in exactly one place and lets Rollup tree-shake the
// entire analytics subgraph for targets that omit `gtm` from
// `VITE_ENABLED_VENDORS` (AC-EX3). `__VENDOR_GTM__` is the build-time boolean
// Vite substitutes from `getVendorDefines(env)`.

type AnalyticsModule = typeof import('@services/analytics/events');

type TrackerName = Exclude<keyof AnalyticsModule, 'clearAnalyticsLedger' | 'pruneAnalyticsLedger'>;

type TrackerInput<K extends TrackerName> = Parameters<AnalyticsModule[K]>[0];

export const fireAnalytics = <K extends TrackerName>(tracker: K, input: TrackerInput<K>): void => {
  if (!__VENDOR_GTM__) return;
  void import('@services/analytics/events').then((m) => {
    const fn = m[tracker] as (i: TrackerInput<K>) => unknown;
    void fn(input);
  });
};

export const clearAnalyticsLedger = (): void => {
  if (!__VENDOR_GTM__) return;
  void import('@services/analytics/events').then((m) => {
    m.clearAnalyticsLedger();
  });
};

export const pruneAnalyticsLedger = (transactionId: string): void => {
  if (!__VENDOR_GTM__) return;
  void import('@services/analytics/events').then((m) => {
    m.pruneAnalyticsLedger(transactionId);
  });
};

export interface AnalyticsApi {
  fire: typeof fireAnalytics;
  clearLedger: typeof clearAnalyticsLedger;
  pruneLedger: typeof pruneAnalyticsLedger;
}

export const useAnalytics = (): AnalyticsApi => ({
  fire: fireAnalytics,
  clearLedger: clearAnalyticsLedger,
  pruneLedger: pruneAnalyticsLedger,
});
